import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  IconAlertTriangle,
  IconCamera,
  IconCheck,
  IconLockOpen,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/stat-card";
import { ScaleInput, TextInput } from "@/components/weighing-form-shared";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { useAuth } from "@/providers/auth-provider";
import { useReadOilPhoto, useReadPlatformPhoto } from "@/api/hooks/use-scale-ocr";
import { usePhotoCategories } from "@/api/hooks/use-photo-categories";
import {
  OIL_TANK_VOL_DETECTION,
  detectionCategories,
  scaleSetDetection,
} from "@/lib/photo-categories";
import {
  SPREAD_GATE,
  PLATFORM_TCG_TOLERANCE,
  OIL_DENSITY_KG_PER_L,
  OIL_LCG_M,
  OIL_TCG_M,
  platformSpread,
  platformTcgRange,
  computePlatform,
  formatSignedKg,
  spread,
  type PlatformReading,
  type PlatformItemInput,
} from "@/domain/weigh-in";
import type { components } from "@/api/generated/schema";
import type { BoatId, PhotoCategoryId } from "@/types/identifiers";

type PlatformWeighing = components["schemas"]["PlatformWeighingResponse-Output"];
type PlatformWeighingCreate = components["schemas"]["PlatformWeighingCreate"];
type PlatformWeighingUpdate = components["schemas"]["PlatformWeighingUpdate"];
type MeasurementInput = components["schemas"]["PlatformMeasurementInput"];
type CustomItemInput = components["schemas"]["PlatformCustomItemInput"];

/** Editable row for a platform custom item. Same shape as the wire type so it
 *  spreads straight into the payload with no casing remap (cf. PlatformReading).
 *  A fully-empty row is ignored; a partially-filled one is caught before save. */
type CustomItemRow = CustomItemInput;

const EMPTY_ITEM: CustomItemRow = {
  label: "",
  weight_kg: 0,
  direction: "subtract",
  lcg_m: 0,
  tcg_m: 0,
};

const itemIsFilled = (r: CustomItemRow): boolean => r.label.trim() !== "" && r.weight_kg > 0;
const itemIsEmpty = (r: CustomItemRow): boolean =>
  r.label.trim() === "" && r.weight_kg === 0 && r.lcg_m === 0 && r.tcg_m === 0;
/** Some content but not a complete label+weight — must be fixed before save so
 *  the row isn't silently dropped. */
const itemIsPartial = (r: CustomItemRow): boolean => !itemIsEmpty(r) && !itemIsFilled(r);

/** Filled rows as calc inputs (net contribution to the live platform result). */
function itemsForCalc(rows: CustomItemRow[]): PlatformItemInput[] {
  return rows.filter(itemIsFilled).map((r) => ({
    weightKg: r.weight_kg,
    lcg: r.lcg_m,
    tcg: r.tcg_m,
    subtract: r.direction === "subtract",
  }));
}

/** Filled rows as the wire payload — the row already matches the wire shape. */
function itemsForPayload(rows: CustomItemRow[]): CustomItemInput[] {
  return rows.filter(itemIsFilled).map((r) => ({ ...r, label: r.label.trim() }));
}

function itemRowsEqual(a: CustomItemRow[], b: CustomItemRow[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => {
    const y = b[i];
    return (
      x.label === y.label &&
      x.weight_kg === y.weight_kg &&
      x.direction === y.direction &&
      x.lcg_m === y.lcg_m &&
      x.tcg_m === y.tcg_m
    );
  });
}

const numOr0 = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
/** Coerce a persisted/draft row into a valid editable row (guards malformed drafts). */
function normalizeItemRow(raw: Partial<CustomItemRow> | undefined): CustomItemRow {
  return {
    label: typeof raw?.label === "string" ? raw.label : "",
    weight_kg: numOr0(raw?.weight_kg),
    direction: raw?.direction === "add" ? "add" : "subtract",
    lcg_m: numOr0(raw?.lcg_m),
    tcg_m: numOr0(raw?.tcg_m),
  };
}

/** A detection photo captured in the form, waiting to be stored as a weighing
 *  photo once the weighing id exists (flushed by the page after save). */
export interface PendingCapture {
  categoryId: PhotoCategoryId;
  label: string;
  file: File;
}

const confTag = (confidence: string) =>
  confidence === "high" ? "✓ high" : confidence === "medium" ? "⚠ medium" : "⚠ low";
const noteTag = (notes: string) => (notes ? ` — ${notes}` : "");

/** Camera chip wrapping a hidden file input — the single capture affordance
 *  for both the per-set Dillon shots and the MD4 oil shot. */
function CaptureChip({
  title,
  idleText,
  pending,
  disabled,
  onFile,
}: {
  title: string;
  idleText: string;
  pending: boolean;
  disabled: boolean;
  onFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <label
      title={title}
      className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider cursor-pointer transition-colors ${
        pending
          ? "bg-blue-50 dark:bg-blue-950/30 border-blue-300 text-blue-700 dark:text-blue-300"
          : "border-input bg-card hover:bg-muted/50 text-muted-foreground hover:text-foreground"
      }`}
    >
      <IconCamera className="w-3 h-3" />
      {pending ? "reading…" : idleText}
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
        disabled={disabled}
      />
    </label>
  );
}

interface Props {
  boatId: BoatId;
  /** Pre-populates fields; edit mode when state is draft, read-only otherwise. */
  initialData?: PlatformWeighing;
  /** Newest weighing id at page open — drafts saved against an older value are discarded. */
  latestWeighingId: string | null;
  /** Signed-off / superseded weighings render the same layout non-editable. */
  readOnly?: boolean;
  isSubmitting: boolean;
  isSigningOff: boolean;
  /** Runs the mutations (create/update, then optional sign-off). Rejects on save
   * failure so the form stays mounted for retry; a null payload means nothing
   * changed in edit mode. `overrideGate` signs off past a failed gate.
   * `captures` are detection photos to store on the weighing after save. */
  onSave: (
    payload: PlatformWeighingCreate | PlatformWeighingUpdate | null,
    withSignOff: boolean,
    overrideGate?: boolean,
    captures?: PendingCapture[],
  ) => Promise<void>;
  /** Navigate back — called after a successful save or on Cancel. */
  onDone: () => void;
  /** Read-only + signed-off only: drop back to draft for editing. */
  onUnlock?: () => void;
  isUnlocking?: boolean;
}

const DRAFT_TTL_MS = 8 * 60 * 60 * 1000;
const SET_INDICES = [1, 2, 3] as const;

interface FormState {
  oilLitres: number;
  notes: string;
  sets: Record<number, PlatformReading>;
  excluded: Record<number, boolean>;
  customItems: CustomItemRow[];
}

const EMPTY_READING: PlatformReading = {
  stbd_aft_kg: 0,
  port_aft_kg: 0,
  stbd_fwd_kg: 0,
  port_fwd_kg: 0,
};

const NONE_EXCLUDED: Record<number, boolean> = { 1: false, 2: false, 3: false };

const EMPTY_STATE: FormState = {
  oilLitres: 0,
  notes: "",
  sets: { 1: { ...EMPTY_READING }, 2: { ...EMPTY_READING }, 3: { ...EMPTY_READING } },
  excluded: { ...NONE_EXCLUDED },
  customItems: [],
};

interface PlatformDraft extends FormState {
  savedAt: number;
  latestWeighingId: string | null;
}

function draftKey(boatId: BoatId, userSub: string): string {
  return `platform-draft-${boatId}-${userSub}`;
}

function loadInitialState(
  boatId: BoatId,
  userSub: string,
  latestWeighingId: string | null,
  initialData?: PlatformWeighing,
): { state: FormState; draftRestored: boolean } {
  if (initialData) {
    const sets: Record<number, PlatformReading> = {
      1: { ...EMPTY_READING },
      2: { ...EMPTY_READING },
      3: { ...EMPTY_READING },
    };
    const excluded: Record<number, boolean> = { ...NONE_EXCLUDED };
    for (const m of initialData.measurements) {
      if (m.set_index >= 1 && m.set_index <= 3) {
        sets[m.set_index] = {
          stbd_aft_kg: m.stbd_aft_kg,
          port_aft_kg: m.port_aft_kg,
          stbd_fwd_kg: m.stbd_fwd_kg,
          port_fwd_kg: m.port_fwd_kg,
        };
        excluded[m.set_index] = m.excluded;
      }
    }
    return {
      state: {
        oilLitres: initialData.oil_kg / OIL_DENSITY_KG_PER_L,
        notes: initialData.notes ?? "",
        sets,
        excluded,
        customItems: (initialData.custom_items ?? []).map((i) => ({ ...i })),
      },
      draftRestored: false,
    };
  }

  const stored = localStorage.getItem(draftKey(boatId, userSub));
  if (stored) {
    try {
      const draft = JSON.parse(stored) as Partial<PlatformDraft>;
      const tooOld = Date.now() - (draft.savedAt ?? 0) > DRAFT_TTL_MS;
      const supersededByNewerSave = (draft.latestWeighingId ?? null) !== latestWeighingId;
      if (tooOld || supersededByNewerSave) {
        localStorage.removeItem(draftKey(boatId, userSub));
      } else if (draft.sets) {
        return {
          state: {
            oilLitres: draft.oilLitres ?? 0,
            notes: draft.notes ?? "",
            sets: {
              1: draft.sets[1] ?? { ...EMPTY_READING },
              2: draft.sets[2] ?? { ...EMPTY_READING },
              3: draft.sets[3] ?? { ...EMPTY_READING },
            },
            excluded: { ...NONE_EXCLUDED, ...(draft.excluded ?? {}) },
            customItems: (draft.customItems ?? []).map(normalizeItemRow),
          },
          draftRestored: true,
        };
      }
    } catch {
      localStorage.removeItem(draftKey(boatId, userSub));
    }
  }

  return { state: EMPTY_STATE, draftRestored: false };
}

function statesEqual(a: FormState, b: FormState): boolean {
  if (a.oilLitres !== b.oilLitres) return false;
  if (a.notes !== b.notes) return false;
  if (!itemRowsEqual(a.customItems, b.customItems)) return false;
  for (const i of SET_INDICES) {
    if (a.excluded[i] !== b.excluded[i]) return false;
    const sa = a.sets[i];
    const sb = b.sets[i];
    if (
      sa.stbd_aft_kg !== sb.stbd_aft_kg ||
      sa.port_aft_kg !== sb.port_aft_kg ||
      sa.stbd_fwd_kg !== sb.stbd_fwd_kg ||
      sa.port_fwd_kg !== sb.port_fwd_kg
    ) {
      return false;
    }
  }
  return true;
}

function fmtPct(frac: number): string {
  return `${(frac * 100).toFixed(3)}%`;
}

function spreadColor(frac: number): string {
  if (frac <= SPREAD_GATE) return "text-emerald-700 dark:text-emerald-400";
  if (frac <= SPREAD_GATE * 2) return "text-amber-700 dark:text-amber-400";
  return "text-red-700 dark:text-red-400";
}

function spreadAccent(frac: number): string {
  if (frac <= SPREAD_GATE) return "border-l-emerald-500";
  if (frac <= SPREAD_GATE * 2) return "border-l-amber-500";
  return "border-l-red-500";
}

function tcgColor(rangeM: number): string {
  if (rangeM <= PLATFORM_TCG_TOLERANCE) return "text-emerald-700 dark:text-emerald-400";
  if (rangeM <= PLATFORM_TCG_TOLERANCE * 2) return "text-amber-700 dark:text-amber-400";
  return "text-red-700 dark:text-red-400";
}

function tcgAccent(rangeM: number): string {
  if (rangeM <= PLATFORM_TCG_TOLERANCE) return "border-l-emerald-500";
  if (rangeM <= PLATFORM_TCG_TOLERANCE * 2) return "border-l-amber-500";
  return "border-l-red-500";
}

const SCALE_ROWS: { label: string; key: keyof PlatformReading; coords: string }[] = [
  { label: "Stbd Aft", key: "stbd_aft_kg", coords: "x=1.65, y=−3.74" },
  { label: "Port Aft", key: "port_aft_kg", coords: "x=1.65, y=+3.74" },
  { label: "Stbd Fwd", key: "stbd_fwd_kg", coords: "x=11.55, y=−3.74" },
  { label: "Port Fwd", key: "port_fwd_kg", coords: "x=11.55, y=+3.74" },
];

export function PlatformWeighingForm({
  boatId,
  initialData,
  latestWeighingId,
  readOnly = false,
  isSubmitting,
  isSigningOff,
  onSave,
  onDone,
  onUnlock,
  isUnlocking = false,
}: Props) {
  const isEditMode = !!initialData;
  const { user } = useAuth();
  const userSub = user.sub;
  const init = useMemo(
    () => loadInitialState(boatId, userSub, latestWeighingId, initialData),
    [boatId, userSub, latestWeighingId, initialData],
  );

  const [state, setState] = useState<FormState>(init.state);

  const didToastRef = useRef(false);
  useEffect(() => {
    if (init.draftRestored && !didToastRef.current) {
      didToastRef.current = true;
      toast.info("Draft restored");
    }
  }, [init.draftRestored]);

  const initialState = useMemo(
    () => (isEditMode ? init.state : EMPTY_STATE),
    [isEditMode, init.state],
  );

  const isDirty = useMemo(() => !statesEqual(state, initialState), [state, initialState]);

  // Draft auto-save (create mode only, debounced). Eagerly clears when the
  // form returns to a clean state so a zeroed-out draft can't resurrect.
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (isEditMode) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    if (!isDirty) {
      localStorage.removeItem(draftKey(boatId, userSub));
      return;
    }
    draftTimerRef.current = setTimeout(() => {
      const draft: PlatformDraft = {
        ...state,
        savedAt: Date.now(),
        latestWeighingId,
      };
      localStorage.setItem(draftKey(boatId, userSub), JSON.stringify(draft));
    }, 500);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [isEditMode, isDirty, state, boatId, userSub, latestWeighingId]);

  const handleConfirmDiscard = useCallback(() => {
    if (!isEditMode) localStorage.removeItem(draftKey(boatId, userSub));
  }, [isEditMode, boatId, userSub]);
  const { markCompleted } = useUnsavedChangesGuard(isDirty, handleConfirmDiscard);

  // Derived values for live feedback — computed over ACTIVE (non-excluded) sets.
  const oilKg = state.oilLitres * OIL_DENSITY_KG_PER_L;
  const activeIndices = useMemo(
    () => SET_INDICES.filter((i) => !state.excluded[i]),
    [state.excluded],
  );
  const activeReadings = useMemo(
    () => activeIndices.map((i) => state.sets[i]),
    [activeIndices, state.sets],
  );
  const spreadReport = useMemo(() => platformSpread(activeReadings), [activeReadings]);
  const calcItems = useMemo(() => itemsForCalc(state.customItems), [state.customItems]);
  const platformResult = useMemo(
    () => computePlatform(activeReadings, oilKg, OIL_LCG_M, OIL_TCG_M, calcItems),
    [activeReadings, oilKg, calcItems],
  );

  // Per-scale spreads (informational — gate uses derived scalars)
  const perScaleSpread = useMemo(() => {
    const result: Record<keyof PlatformReading, number> = {
      stbd_aft_kg: 0,
      port_aft_kg: 0,
      stbd_fwd_kg: 0,
      port_fwd_kg: 0,
    };
    for (const k of Object.keys(result) as (keyof PlatformReading)[]) {
      result[k] = spread(activeReadings.map((r) => r[k]));
    }
    return result;
  }, [activeReadings]);

  // Transverse repeatability is gated on the absolute TCG range (My/weight),
  // not a relative My spread (degenerate at the balanced TCG~0 target).
  const tcgRange = useMemo(() => platformTcgRange(activeReadings), [activeReadings]);
  const gateOk =
    spreadReport.weight <= SPREAD_GATE &&
    spreadReport.mx <= SPREAD_GATE &&
    tcgRange <= PLATFORM_TCG_TOLERANCE;

  // When the gate fails, point at the set whose removal would clear it (the
  // operator confirms by toggling Exclude — we never auto-drop a set).
  const suggestedSetIndex = useMemo(() => {
    if (gateOk || activeIndices.length < 3) return null;
    const clears = (rs: PlatformReading[]) => {
      const sr = platformSpread(rs);
      return (
        sr.weight <= SPREAD_GATE &&
        sr.mx <= SPREAD_GATE &&
        platformTcgRange(rs) <= PLATFORM_TCG_TOLERANCE
      );
    };
    for (let p = 0; p < activeReadings.length; p++) {
      if (clears(activeReadings.filter((_, j) => j !== p))) return activeIndices[p];
    }
    return null;
  }, [gateOk, activeReadings, activeIndices]);

  const activeFilled = activeReadings.every(
    (r) => r.stbd_aft_kg > 0 && r.port_aft_kg > 0 && r.stbd_fwd_kg > 0 && r.port_fwd_kg > 0,
  );
  const enoughActive = activeIndices.length >= 2;
  const gateEvaluable = enoughActive && activeFilled;
  // Oil is a hard requirement (0 = not entered); a failed gate is not — it can
  // be signed off with an explicit override, confirmed in handleSubmit.
  const oilEntered = state.oilLitres > 0;
  // A row with some content but no label+weight would be silently dropped on
  // save — block until it's completed or cleared.
  const hasPartialItems = useMemo(
    () => state.customItems.some(itemIsPartial),
    [state.customItems],
  );
  const canSignOff = enoughActive && activeFilled && oilEntered && !hasPartialItems;
  const needsOverride = gateEvaluable && !gateOk;

  const updateCell = (setIndex: number, key: keyof PlatformReading, value: number) => {
    setState((prev) => ({
      ...prev,
      sets: { ...prev.sets, [setIndex]: { ...prev.sets[setIndex], [key]: value } },
    }));
  };

  const setReadingForSet = (setIndex: number, reading: PlatformReading) => {
    setState((prev) => ({
      ...prev,
      sets: { ...prev.sets, [setIndex]: reading },
    }));
  };

  const addCustomItem = () => {
    setState((prev) => ({ ...prev, customItems: [...prev.customItems, { ...EMPTY_ITEM }] }));
  };

  const updateCustomItem = (index: number, patch: Partial<CustomItemRow>) => {
    setState((prev) => ({
      ...prev,
      customItems: prev.customItems.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const removeCustomItem = (index: number) => {
    setState((prev) => ({
      ...prev,
      customItems: prev.customItems.filter((_, i) => i !== index),
    }));
  };

  const ocr = useReadPlatformPhoto();
  const oilOcr = useReadOilPhoto();
  const [ocrTargetSet, setOcrTargetSet] = useState<number | null>(null);

  // Category slots that double as detection targets (Weigh in #N, Oil level
  // MD4). A successful capture is kept here and stored as a weighing photo
  // after save — one shot serves both detection and documentation.
  const { data: photoCategories } = usePhotoCategories();
  const detectionCats = useMemo(() => detectionCategories(photoCategories), [photoCategories]);
  // Read through a ref inside the async OCR handlers: the categories query may
  // resolve during the Gemini round trip, and the in-flight closure would
  // otherwise still see the pre-load empty map and silently drop the capture.
  const detectionCatsRef = useRef(detectionCats);
  detectionCatsRef.current = detectionCats;
  const pendingCaptures = useRef<Map<string, PendingCapture>>(new Map());

  const keepCapture = (detection: string, file: File) => {
    const cat = detectionCatsRef.current.get(detection);
    if (!cat) {
      // No category carries this detection flag (unseeded environment or
      // admin removed the slot) — the reading was applied, but say so
      // instead of silently dropping the evidence photo.
      console.warn(`No photo category carries detection "${detection}" — capture not stored`);
      toast.warning("Reading applied — photo not stored (no capture slot configured)");
      return;
    }
    pendingCaptures.current.set(cat.id, { categoryId: cat.id, label: cat.name, file });
  };

  const handlePhotoForSet = (setIndex: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setOcrTargetSet(setIndex);
    try {
      const r = await ocr.mutateAsync(file);
      // Sum-of-four checksum guard — flag if the model's own total doesn't match
      const sum = r.stbd_aft_kg + r.stbd_fwd_kg + r.port_aft_kg + r.port_fwd_kg;
      const drift = Math.abs(sum - r.total_kg);
      setReadingForSet(setIndex, {
        stbd_aft_kg: r.stbd_aft_kg,
        port_aft_kg: r.port_aft_kg,
        stbd_fwd_kg: r.stbd_fwd_kg,
        port_fwd_kg: r.port_fwd_kg,
      });
      keepCapture(scaleSetDetection(setIndex), file);
      const driftTag = drift > 1 ? ` · checksum drift ${drift.toFixed(1)}kg` : "";
      toast.success(
        `Set ${setIndex} read from photo (${confTag(r.confidence)}${driftTag})${noteTag(r.notes)}`,
        { duration: r.confidence === "high" && drift < 1 ? 4000 : 12000 },
      );
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setOcrTargetSet(null);
    }
  };

  const handleOilPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const r = await oilOcr.mutateAsync(file);
      setState((prev) => ({ ...prev, oilLitres: r.tank_vol_l }));
      keepCapture(OIL_TANK_VOL_DETECTION, file);
      toast.success(
        `Tank Vol ${r.tank_vol_l} L read from the MD4 (${confTag(r.confidence)})${noteTag(r.notes)}`,
        { duration: r.confidence === "high" ? 4000 : 12000 },
      );
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const buildPayload = useCallback((): PlatformWeighingCreate | PlatformWeighingUpdate | null => {
    const oilKgOut = state.oilLitres * OIL_DENSITY_KG_PER_L;
    const measurementsArray: MeasurementInput[] = SET_INDICES.map((i) => ({
      set_index: i,
      ...state.sets[i],
      excluded: state.excluded[i],
    }));
    const itemsOut = itemsForPayload(state.customItems);

    if (isEditMode && initialData) {
      const update: PlatformWeighingUpdate = {};
      if (oilKgOut !== initialData.oil_kg) {
        update.oil_kg = oilKgOut;
        update.oil_lcg = OIL_LCG_M;
        update.oil_tcg = OIL_TCG_M;
      }
      if (state.notes !== (initialData.notes ?? "")) update.notes = state.notes;

      // Measurements ship as one atomic array — if any cell OR exclusion changed, send all sets.
      const measurementsChanged = SET_INDICES.some((i) => {
        const initial = initialData.measurements.find((m) => m.set_index === i);
        const current = state.sets[i];
        if (!initial) return true;
        return (
          initial.stbd_aft_kg !== current.stbd_aft_kg ||
          initial.port_aft_kg !== current.port_aft_kg ||
          initial.stbd_fwd_kg !== current.stbd_fwd_kg ||
          initial.port_fwd_kg !== current.port_fwd_kg ||
          initial.excluded !== state.excluded[i]
        );
      });
      if (measurementsChanged) update.measurements = measurementsArray;

      // Custom items ship wholesale too — send the full list when it differs
      // from what's persisted (rows already match the wire shape).
      if (!itemRowsEqual(itemsOut, initialData.custom_items ?? [])) {
        update.custom_items = itemsOut;
      }

      return Object.keys(update).length === 0 ? null : update;
    }

    return {
      oil_kg: oilKgOut,
      oil_lcg: OIL_LCG_M,
      oil_tcg: OIL_TCG_M,
      notes: state.notes || null,
      measurements: measurementsArray,
      custom_items: itemsOut,
    };
  }, [state, isEditMode, initialData]);

  const handleSubmit = useCallback(
    async (withSignOff: boolean) => {
      const overrideGate = withSignOff && gateEvaluable && !gateOk;
      if (overrideGate) {
        const failed = [
          spreadReport.weight > SPREAD_GATE &&
            `• weight spread ${fmtPct(spreadReport.weight)} (limit 0.1%)`,
          spreadReport.mx > SPREAD_GATE && `• Mx spread ${fmtPct(spreadReport.mx)} (limit 0.1%)`,
          tcgRange > PLATFORM_TCG_TOLERANCE &&
            `• TCG range ±${(tcgRange * 1000).toFixed(1)} mm (limit ${(PLATFORM_TCG_TOLERANCE * 1000).toFixed(0)} mm)`,
        ]
          .filter(Boolean)
          .join("\n");
        if (
          !window.confirm(
            `The repeatability gate is not met:\n${failed}\n\n` +
              "Sign off anyway? The override is recorded in the audit log " +
              "and the weighing will be marked out-of-gate.",
          )
        ) {
          return;
        }
      }
      try {
        await onSave(
          buildPayload(),
          withSignOff,
          overrideGate,
          [...pendingCaptures.current.values()],
        );
        pendingCaptures.current.clear();
        markCompleted();
        localStorage.removeItem(draftKey(boatId, userSub));
        onDone();
      } catch {
        // Stay on the page; the action hook toasted the error.
      }
    },
    [
      onSave,
      buildPayload,
      markCompleted,
      boatId,
      userSub,
      onDone,
      gateEvaluable,
      gateOk,
      spreadReport,
      tcgRange,
    ],
  );

  // OCR in flight blocks save/sign-off too: the capture is only recorded when
  // the read resolves, so submitting mid-read would sign off without it.
  const busy = isSubmitting || isSigningOff || ocr.isPending || oilOcr.isPending;

  return (
    <div>
      {/* Live derived output — stat card row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Weight"
          value={platformResult ? `${platformResult.weight.toFixed(1)} kg` : "—"}
          accent={platformResult ? spreadAccent(spreadReport.weight) : "border-l-border"}
          secondary={platformResult ? `spread ${fmtPct(spreadReport.weight)}` : undefined}
          secondaryClass={platformResult ? spreadColor(spreadReport.weight) : undefined}
        />
        <StatCard
          label="LCG"
          value={platformResult ? `${platformResult.lcg.toFixed(3)} m` : "—"}
          accent={platformResult ? spreadAccent(spreadReport.mx) : "border-l-border"}
          secondary={platformResult ? `Mx ${fmtPct(spreadReport.mx)}` : undefined}
          secondaryClass={platformResult ? spreadColor(spreadReport.mx) : undefined}
        />
        <StatCard
          label="TCG"
          value={platformResult ? `${platformResult.tcg.toFixed(3)} m` : "—"}
          accent={platformResult ? tcgAccent(tcgRange) : "border-l-border"}
          secondary={platformResult ? `range ±${(tcgRange * 1000).toFixed(1)} mm` : undefined}
          secondaryClass={platformResult ? tcgColor(tcgRange) : undefined}
        />
        <StatCard
          label="Sign-off gate"
          value={!gateEvaluable ? "—" : gateOk ? "PASS" : "NOT MET"}
          accent={
            !gateEvaluable ? "border-l-border" : gateOk ? "border-l-emerald-500" : "border-l-red-500"
          }
          secondary={
            !gateEvaluable
              ? !enoughActive
                ? "need ≥2 active sets"
                : "fill the active sets"
              : gateOk
                ? `${activeIndices.length} active set${activeIndices.length === 1 ? "" : "s"} within gate`
                : !readOnly && suggestedSetIndex !== null
                  ? `Set ${suggestedSetIndex} looks like the outlier`
                  : "exclude a set, re-measure, or override"
          }
          secondaryClass={
            !gateEvaluable
              ? undefined
              : gateOk
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-red-700 dark:text-red-400"
          }
        />
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Derived live — averaged over {activeIndices.length} active set
        {activeIndices.length === 1 ? "" : "s"}, net of {oilKg.toFixed(1)} kg oil
        {calcItems.length > 0
          ? ` and ${calcItems.length} custom item${calcItems.length === 1 ? "" : "s"}`
          : ""}
        .
      </p>

      {/* Strap readings — one accent card per set */}
      <div className="mt-5">
        <div className="mb-2 flex items-baseline justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Strap readings (kg)
          </div>
          <span className="text-[11px] text-muted-foreground">
            {activeIndices.length} of {SET_INDICES.length} sets included
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {SET_INDICES.map((i) => {
            const excluded = state.excluded[i];
            const suggested = !readOnly && suggestedSetIndex === i;
            const r = state.sets[i];
            const total = r.stbd_aft_kg + r.port_aft_kg + r.stbd_fwd_kg + r.port_fwd_kg;
            const accent = excluded
              ? "border-l-border"
              : suggested
                ? "border-l-amber-400"
                : "border-l-emerald-500";
            const isPendingOcr = ocr.isPending && ocrTargetSet === i;
            return (
              <div
                key={i}
                className={`bg-card border-l-3 ${accent} rounded-r-lg pl-4 pr-4 py-3 ${
                  excluded ? "opacity-70" : ""
                }`}
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div
                    className={`text-sm font-bold ${
                      excluded ? "text-muted-foreground line-through" : ""
                    }`}
                  >
                    Set {i}
                    {suggested && (
                      <span
                        className="ml-1 text-amber-600 dark:text-amber-400"
                        title="Likely outlier — consider excluding"
                      >
                        ⚠
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {!readOnly && (
                      <CaptureChip
                        title="Fill this set from a scale photo"
                        idleText="photo"
                        pending={isPendingOcr}
                        disabled={ocr.isPending}
                        onFile={handlePhotoForSet(i)}
                      />
                    )}
                    <button
                      type="button"
                      disabled={readOnly}
                      onClick={() =>
                        setState((p) => ({
                          ...p,
                          excluded: { ...p.excluded, [i]: !p.excluded[i] },
                        }))
                      }
                      title={
                        readOnly
                          ? undefined
                          : excluded
                            ? "Excluded — click to include"
                            : "Included — click to exclude from calculations"
                      }
                      className={`rounded border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors disabled:cursor-default ${
                        excluded
                          ? "border-input bg-muted text-muted-foreground"
                          : suggested
                            ? "border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300"
                            : "border-emerald-500/60 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300"
                      }`}
                    >
                      {excluded ? "excluded" : suggested ? "exclude?" : "included"}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {SCALE_ROWS.map((row) => (
                    <label key={row.key} className="flex items-center justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block text-[11px] font-semibold text-foreground">
                          {row.label}
                        </span>
                        <span className="block text-[10px] tabular-nums text-muted-foreground">
                          {row.coords}
                        </span>
                      </span>
                      <span className="w-24 shrink-0">
                        <ScaleInput
                          value={r[row.key]}
                          onChange={(v) => updateCell(i, row.key, v)}
                          disabled={readOnly}
                        />
                      </span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex items-baseline justify-between border-t border-border pt-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Set total
                  </span>
                  <span className="text-sm font-bold tabular-nums">
                    {total > 0 ? total.toFixed(1) : "—"}{" "}
                    <span className="text-[11px] font-medium text-muted-foreground">kg</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Per-scale spread across included sets:{" "}
          {SCALE_ROWS.map((row, idx) => (
            <span key={row.key}>
              {row.label}{" "}
              <span
                className={`font-semibold tabular-nums ${
                  perScaleSpread[row.key] > 0
                    ? spreadColor(perScaleSpread[row.key])
                    : "text-muted-foreground/60"
                }`}
              >
                {fmtPct(perScaleSpread[row.key])}
              </span>
              {idx < SCALE_ROWS.length - 1 ? " · " : ""}
            </span>
          ))}
          <span className="text-muted-foreground/70">
            {" "}
            — informational; the sign-off gate checks W + Mx spread (≤ 0.1%) and TCG range (≤ 5 mm).
          </span>
        </p>
      </div>

      {/* Oil + notes */}
      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Oil installed at measurement time
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="flex items-center gap-1.5">
              <span className="w-20">
                <ScaleInput
                  value={state.oilLitres}
                  onChange={(v) => setState((p) => ({ ...p, oilLitres: v }))}
                  disabled={readOnly}
                />
              </span>
              <span className="text-xs font-medium text-muted-foreground">litres</span>
            </span>
            {!readOnly && (
              <CaptureChip
                title="Read Tank Vol from an MD4 photo"
                idleText="MD4 photo"
                pending={oilOcr.isPending}
                disabled={oilOcr.isPending}
                onFile={handleOilPhoto}
              />
            )}
            <div className="text-xs tabular-nums text-muted-foreground">
              = <span className="font-semibold text-foreground">{oilKg.toFixed(1)} kg</span>
              <span className="text-muted-foreground/70">
                {" "}
                · {OIL_DENSITY_KG_PER_L} kg/L · LCG {OIL_LCG_M} m · TCG {OIL_TCG_M} m
              </span>
            </div>
          </div>
        </div>

        <div className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Notes{" "}
            {!readOnly && (
              <span className="font-medium normal-case tracking-normal text-muted-foreground/70">
                (optional)
              </span>
            )}
          </div>
          {readOnly ? (
            <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">
              {state.notes || <span className="text-muted-foreground">—</span>}
            </p>
          ) : (
            <textarea
              value={state.notes}
              onChange={(e) => setState((p) => ({ ...p, notes: e.target.value }))}
              placeholder="e.g. Hull #1 post-rebuild"
              className="mt-2 w-full p-2 text-sm border border-input bg-card rounded-md min-h-[60px] focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            />
          )}
        </div>
      </div>

      {/* Custom items — free-form masses folded into the net platform result */}
      <div className="mt-5">
        <div className="mb-2 flex items-baseline justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Custom items{" "}
            <span className="font-medium normal-case tracking-normal text-muted-foreground/70">
              (subtract what sat on the boat, add what was missing)
            </span>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={addCustomItem}
              className="inline-flex items-center gap-1 rounded border border-input bg-card px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            >
              <IconPlus className="h-3 w-3" /> Add item
            </button>
          )}
        </div>

        {state.customItems.length === 0 ? (
          <div className="bg-card border-l-3 border-l-border rounded-r-lg px-4 py-3 text-[11px] text-muted-foreground">
            {readOnly ? "No custom items recorded." : "No custom items — add one if a mass on the platform isn't part of the bare boat."}
          </div>
        ) : (
          <div className="space-y-2">
            {state.customItems.map((row, index) => {
              const subtract = row.direction === "subtract";
              const signed = formatSignedKg(row.weight_kg, subtract);
              if (readOnly) {
                return (
                  <div
                    key={index}
                    className={`bg-card border-l-3 ${subtract ? "border-l-rose-400" : "border-l-emerald-500"} rounded-r-lg pl-4 pr-4 py-2.5`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-semibold">{row.label}</span>
                      <span className="text-sm font-bold tabular-nums">{signed}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                      {subtract ? "on boat (subtracted)" : "missing (added)"} · LCG {row.lcg_m} m ·
                      TCG {row.tcg_m} m
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={index}
                  className={`bg-card border-l-3 ${subtract ? "border-l-rose-400" : "border-l-emerald-500"} rounded-r-lg pl-4 pr-3 py-2.5`}
                >
                  <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                    <label className="min-w-[8rem] flex-1">
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Label
                      </span>
                      <span className="mt-1 block">
                        <TextInput
                          value={row.label}
                          onChange={(v) => updateCustomItem(index, { label: v })}
                          placeholder="e.g. Battery, jockey pump"
                          maxLength={100}
                        />
                      </span>
                    </label>
                    <div>
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Direction
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          updateCustomItem(index, { direction: subtract ? "add" : "subtract" })
                        }
                        title={
                          subtract
                            ? "On the boat during the weigh — subtracted to get the bare platform. Click to switch to added."
                            : "Absent during the weigh — added to the bare platform. Click to switch to subtracted."
                        }
                        className={`mt-1 inline-flex items-center gap-1 rounded border px-2 py-1.5 text-xs font-semibold transition-colors ${
                          subtract
                            ? "border-rose-400/60 bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300"
                            : "border-emerald-500/60 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300"
                        }`}
                      >
                        {subtract ? "− subtract" : "+ add"}
                      </button>
                    </div>
                    <label className="w-20">
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Weight
                      </span>
                      <span className="mt-1 block">
                        <ScaleInput
                          value={row.weight_kg}
                          onChange={(v) => updateCustomItem(index, { weight_kg: v })}
                        />
                      </span>
                    </label>
                    <label className="w-16">
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        LCG m
                      </span>
                      <span className="mt-1 block">
                        <ScaleInput
                          value={row.lcg_m}
                          onChange={(v) => updateCustomItem(index, { lcg_m: v })}
                        />
                      </span>
                    </label>
                    <label className="w-16">
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        TCG m
                      </span>
                      <span className="mt-1 block">
                        <ScaleInput
                          value={row.tcg_m}
                          onChange={(v) => updateCustomItem(index, { tcg_m: v })}
                        />
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => removeCustomItem(index)}
                      title="Remove this item"
                      className="mb-0.5 rounded border border-input bg-card p-1.5 text-muted-foreground hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-600 dark:hover:text-rose-400"
                    >
                      <IconTrash className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Signed by direction and folded into the net platform weight, LCG and TCG (like oil).
          Vertical arm is not modelled — platform VCG is fixed. Items don't affect the repeatability
          gate.
        </p>
      </div>

      {/* Action bar */}
      {readOnly ? (
        onUnlock && (
          <div className="mt-6 flex items-center gap-2 border-t border-border pt-4">
            <Button variant="outline" onClick={onUnlock} disabled={isUnlocking}>
              <IconLockOpen className="w-4 h-4 mr-1.5" />
              {isUnlocking ? "Unlocking…" : "Unlock to edit"}
            </Button>
          </div>
        )
      ) : (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          <div
            className={`mr-auto inline-flex items-center gap-1.5 text-xs font-medium ${
              hasPartialItems
                ? "text-amber-700 dark:text-amber-400"
                : !gateEvaluable
                  ? "text-muted-foreground"
                  : !oilEntered || !gateOk
                    ? !gateOk
                      ? "text-red-700 dark:text-red-400"
                      : "text-amber-700 dark:text-amber-400"
                    : "text-emerald-700 dark:text-emerald-400"
            }`}
          >
            {hasPartialItems ? (
              <IconAlertTriangle className="w-3.5 h-3.5 shrink-0" />
            ) : gateEvaluable ? (
              gateOk && oilEntered ? (
                <IconCheck className="w-3.5 h-3.5 shrink-0" />
              ) : (
                <IconAlertTriangle className="w-3.5 h-3.5 shrink-0" />
              )
            ) : null}
            {hasPartialItems
              ? "Complete or clear the custom item rows — each needs a label and a weight"
              : !gateEvaluable
                ? !enoughActive
                  ? "Need at least 2 active (non-excluded) sets to sign off"
                  : "Fill all cells of the active sets to evaluate the sign-off gate"
                : !gateOk
                  ? `Gate not met — W/Mx ${fmtPct(Math.max(spreadReport.weight, spreadReport.mx))} (≤ 0.1%) · TCG ±${(tcgRange * 1000).toFixed(1)} mm (≤ ${(PLATFORM_TCG_TOLERANCE * 1000).toFixed(0)} mm)`
                  : !oilEntered
                    ? "Enter the hydraulic oil litres to sign off"
                    : `Ready to sign off — ${activeIndices.length} active sets within the gate`}
          </div>
          <Button variant="ghost" onClick={onDone} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => void handleSubmit(false)}
            disabled={busy || hasPartialItems}
            title={hasPartialItems ? "Complete or clear the custom item rows first" : undefined}
          >
            {isSubmitting ? "Saving…" : isEditMode ? "Update draft" : "Save draft"}
          </Button>
          <Button
            className={
              needsOverride
                ? "bg-amber-600 hover:bg-amber-700 text-white"
                : "bg-emerald-600 hover:bg-emerald-700 text-white"
            }
            onClick={() => void handleSubmit(true)}
            disabled={busy || !canSignOff}
            title={
              hasPartialItems
                ? "Complete or clear the custom item rows first"
                : !oilEntered
                  ? "Enter the hydraulic oil litres before sign-off"
                  : !canSignOff
                    ? "Need at least 2 filled active sets"
                    : needsOverride
                      ? "Repeatability gate not met — signs off with a recorded override"
                      : undefined
            }
          >
            {isSigningOff
              ? "Signing off…"
              : needsOverride
                ? "Sign off anyway"
                : "Save & sign off"}
          </Button>
        </div>
      )}
    </div>
  );
}
